import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Product } from '@app/core/business/product/product';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, defaultIfEmpty, delay, map, mapTo, takeUntil, tap, timeout } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { ProductAvailability } from './product-availability';
import { ElasticSearchService } from '../elastic-search/elastic-serch.service';
import { AppConfigurationService, AvailabilityConfiguration } from '@app/core/service/app-configuration.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private baseUrl: string = environment.baseApiUrl;
  private baseAvailabilityUrl: string = environment.baseAvailabilityApiUrl;
  private Products: Product[] = [];
  private useTestElkConfig = false;
  public msElaspsed: number = 0;
  public dateInit = new Date();
  public availabilityConfiguration: AvailabilityConfiguration = { timeout: 3000, defaultState: 2 };

  constructor(private http: HttpClient, private cacheService: CacheService, private elasticSearchService: ElasticSearchService, private appConfigurationService: AppConfigurationService) {
    this.elasticSearchService.getUseTestElkConfig$().subscribe(value => {
      this.useTestElkConfig = value;
    });
    this.appConfigurationService.getAvailabilityConfig$().subscribe(config => {
      this.availabilityConfiguration = config;
    });
  }



  /**
 * Get product details (standard or date courte)
 * @param companyCode Company code
 * @param productCode Product code
 * @param isDateCourte Whether to use the date courte endpoint
 * @param customerCode Customer code
 * @param orderCode Order code
 * @param isDispo Availability flag (used only for date courte)
 */
  public getProduct(
    companyCode: string,
    productCode: string,
    isDateCourte: boolean,
    customerCode?: string,
    orderCode?: string,
    isDispo?: boolean
  ): Observable<Product> {

    // Gestion du mock
    if (environment.useMock) {
      const product = this.Products.find((p: Product) => p.code === productCode);
      return of(product ?? new Product()).pipe(delay(200));
    }

    // Cas où il n'y a pas de commande en cours
    if (!orderCode) orderCode = "";

    // Construction des paramètres
    const params: any = {
      customerCode,
      orderCode,
      timestamp: this.cacheService.getTimestamp()
    };

    if (isDateCourte && isDispo !== undefined) {
      params.isDispo = isDispo;
    }

    // Construction de l'endpoint
    const endpoint = isDateCourte
      ? `datecourte/${productCode}`
      : `${productCode}`;

    // Requête HTTP
    return this.http.get<Product>(
      `${this.baseUrl}/companies/${companyCode}/products/${endpoint}`,
      { params }
    );
  }



  /**
   * Add or remove favorite flag on product for a company and a contact
   * @param productCode Code of the product
   */
  public postFavorite(companyCode: string, productCode: string, add: boolean): Observable<any> {
    this.cacheService.invalidate();
    if (environment.useMock) {
      const products = this.Products.filter((p: Product) => {
        return p.code === productCode;
      });

      products[0].isFavorite = add;
      return of(products[0]).pipe(delay(200));
    }

    //Ici il faudrait mettre à jour l'offre ...

    return this.http.post(`${this.baseUrl}/companies/${companyCode}/products/${productCode}/favorite`,
      { add: add, contactCode: '0' });
  }

  /**
   * Get product availability
   * @param companyCode Company code
   * @param productCode Product code
   * @param customerCode Customer code
   * @param orderCode Order code
   */
  public getProductAvailability(
    companyCode: string,
    productCode: string,
    customerCode: string,
    isDateCourte: boolean,
    orderCode?: string
  ): Observable<ProductAvailability> {

    // Cas où il n'y a pas de commande en cours
    if (orderCode == null)
      orderCode = "";
    const params = { customerCode: customerCode, orderCode: orderCode };

    const defaultAvailability: ProductAvailability = { availability: this.availabilityConfiguration.defaultState }; // Timeout → dispo
    const errorAvailability: ProductAvailability = { availability: 1 };   // Erreur → Quantité limitée
    const endOfRoute = isDateCourte ? 'availabilityDC' : 'availability';


    const url =
      companyCode === 'VITAFRAIS'
        ? `${this.baseUrl}/companies/${companyCode}/products/${productCode}/${endOfRoute}`
        : `${this.baseAvailabilityUrl}/companies/${companyCode}/products/${productCode}/${endOfRoute}`;


    // ⏱ timer qui déclenche après 3 secondes
    const cancel$ = timer(this.availabilityConfiguration.timeout);

    return this.http.get<ProductAvailability>(url, { params }).pipe(
      takeUntil(cancel$), // annule la requête HTTP au bout de 3 s → ferme la socket réseau et provoque un cancelled. Ne part pas dans le CatchError..
      catchError(err => {
        console.error('Erreur lors de la récupération de la disponibilité produit', err);
        return of(errorAvailability); // Quand on récupère une erreur, on renvoie une availability par défaut.
      }),
      defaultIfEmpty(defaultAvailability), // ⏱ Timeout → renvoie la valeur par défaut
      );
  }


  /**
   * Recherche d'un produit.
   * @param companyCode
   * @param keywords 
   * @param useLoader Affiche le loader global ou non sur la recherch
   * @param customerCode
   * @param orderCode 
   * @param numberMaxProducts Nombre max de produits à retourner
   */
  public getProductSearch(
    companyCode: string,
    keywords: string,
    useLoader: boolean,
    customerCode?: string,
    orderCode?: string,
    numberMaxProducts?: number
  ): Observable<Product[]> {
    // Par défaut, si aucune commande en cours
    if (!orderCode) orderCode = "";

    const endpoint = useLoader ? "search" : "search-no-loader";

    const params = {
      customerCode: customerCode,
      useTestConfig: this.useTestElkConfig,
      orderCode: orderCode,
      numberMaxProducts: numberMaxProducts
    };

    const start = new Date().getTime();

    return this.http.get<Product[]>(
      `${this.baseUrl}/companies/${companyCode}/products/${endpoint}/${keywords}`,
      { params }
    ).pipe(map(products => {
      this.msElaspsed = new Date().getTime() - start;
      return products;
    }));
  }









  /**
   * Return scanned product
   * @param eanCode Ean code
   * @param customerCode Customer code
   * @param orderCode Order code
   */
  public getScanProduct(
    eanCode: string,
    companyCode: string,
    customerCode: string,
    orderCode: string
  ): Observable<Product> {
    const params = { customerCode: customerCode, orderCode: orderCode };
    return this.http.get<Product>(
      `${this.baseUrl}/companies/${companyCode}/products/scan/${eanCode}`,
      { params: params }
    );
  }

  /**
   * Return count Favorites
   * @param customerCode Customer code
   */
  public getCountFavorites(companyCode: string, customerCode: string, orderCode?: string): Observable<number> {
    //Cas où il n'y a pas de commande en cours
    if (orderCode == null)
      orderCode = "";

    const params = { customerCode: customerCode, orderCode: orderCode };

    return this.http.get<number>(`${this.baseUrl}/companies/${companyCode}/Offers/Favorites`,
      { params: params });
  }

}
